import { useCallback, useMemo } from 'react';
import { useDorisData } from 'src/utils/Hooks/useDorisData';

export interface DorisTableDataRecord {
  fieldKey: string;
  totalClicks: number;
  organicClicks: number;
  adClicks: number;
  otherClicks: number;
  adSpend: number;
  weekId?: number;
  metadata?: { stacklineSku: string };
}

export interface DorisTableDataResponse {
  fieldName: string;
  records: DorisTableDataRecord[];
}

export interface UseDorisTableDataProps {
  /**
   * API method name to call (e.g., 'GetTrafficCountSummaryByType', 'keymetrics')
   */
  apiMethodName?: string;

  /**
   * Field name to group by (such as 'keyword', 'brand', etc.)
   */
  groupByFieldName: string;

  /**
   * Page number for pagination
   */
  pageNumber?: number;

  /**
   * Items per page for pagination
   */
  itemsPerPage?: number;

  /**
   * Sort field name
   */
  sortFieldName?: string;

  /**
   * Custom start week ID
   */
  startWeekId?: number;

  /**
   * Custom end week ID
   */
  endWeekId?: number;

  /**
   * Custom comparison start week ID
   */
  customComparisonStartWeekId?: number;

  /**
   * Custom comparison end week ID
   */
  customComparisonEndWeekId?: number;

  /**
   * Metric type (e.g., 'adClicks')
   */
  metricType: string;

  /**
   * Brand IDs to filter by
   */
  brandIds?: number[];

  /**
   * Category IDs to filter by
   */
  categoryIds?: number[];

  /**
   * Subcategory IDs to filter by - can be either string or number
   */
  subcategoryIds?: (string | number)[];

  /**
   * Segment IDs to filter by
   */
  segmentIds?: string[];

  /**
   * Minimum retail price
   */
  minRetailPrice?: number;

  /**
   * Maximum retail price
   */
  maxRetailPrice?: number;

  /**
   * Function to filter the response
   */
  filterResponse?: (row: any) => boolean;

  /**
   * Optional aggregation key override
   */
  aggregationKey?: string;

  /**
   * Fields to display
   */
  fields?: {
    name: string;
    displayName: string;
  }[];

  /**
   * Whether to perform the fetch
   */
  shouldPerformFetch?: boolean;

  /**
   * Additional parameters to pass to the API
   */
  additionalParams?: Record<string, any>;
}

/**
 * Hook for fetching data from the TrafficCountSummaryByType API
 * to display in tables/grids throughout the site.
 */
export const useDorisTableData = ({
  apiMethodName = 'GetTrafficCountSummaryByType',
  groupByFieldName,
  itemsPerPage = 20,
  metricType = 'adClicks',
  brandIds,
  categoryIds,
  subcategoryIds,
  segmentIds,
  minRetailPrice,
  maxRetailPrice,
  additionalParams,
  shouldPerformFetch = true,
  fields,
  filterResponse
}: UseDorisTableDataProps) => {
  const normalizedSubcategoryIds = useMemo(() => {
    if (!subcategoryIds) return undefined;
    return subcategoryIds.map((id) => (typeof id === 'string' ? parseInt(id, 10) : id));
  }, [subcategoryIds]);

  const { data, comparisonData, isLoading } = useDorisData<DorisTableDataResponse>({
    methodName: apiMethodName,
    metricType,
    fieldName: groupByFieldName,
    // currently backend does not support pagination yet, so we need to fetch a lot more than we need
    resultCount: itemsPerPage * 50,
    categoryIds,
    subcategoryIds: normalizedSubcategoryIds,
    brandIds,
    segmentIds,
    minRetailPrice,
    maxRetailPrice,
    enabled: shouldPerformFetch,
    additionalParams: {
      ...additionalParams
    }
  });

  const parsedData = useMemo(() => {
    if (isLoading || !data || !data.records) {
      return [];
    }

    return data.records
      .filter((row) => (filterResponse ? filterResponse(row) : true))
      .map((row) => {
        const field_value = fields?.reduce((acc, field) => {
          acc[`${field.name}_count`] = row[field.name as keyof DorisTableDataRecord];
          return acc;
        }, {} as Record<string, any>);
        return {
          ...row,
          name: row.fieldKey,
          metadata: { ...row.metadata, name: row.fieldKey },
          ...field_value
        };
      });
  }, [data, fields, filterResponse, isLoading]);

  // Helper function to get values by field name
  const getValuesByFieldName = useCallback(
    (fieldName: string) => {
      return parsedData.map((row) => row[fieldName as keyof DorisTableDataRecord]);
    },
    [parsedData]
  );

  // Helper function to get comparison values by field name
  const getComparisonValuesByFieldName = useCallback(
    (fieldName: string) => {
      if (!comparisonData || !comparisonData.records) {
        return parsedData.map(() => null);
      }

      const mappedField = fieldName;

      // Create a lookup map for comparison data by fieldKey
      const comparisonMap = new Map<string, DorisTableDataRecord>();
      comparisonData.records.forEach((record) => {
        comparisonMap.set(record.fieldKey, record);
      });

      // Map each primary record to its corresponding comparison value (or null if not found)
      return parsedData.map((record) => {
        const comparisonRecord = comparisonMap.get(record.fieldKey);
        return comparisonRecord ? comparisonRecord[mappedField as keyof DorisTableDataRecord] : null;
      });
    },
    [comparisonData, parsedData]
  );

  const getValueDifferencesByFieldName = useCallback(
    (fieldName: string) => {
      const values = getValuesByFieldName(fieldName);
      const compareValues = getComparisonValuesByFieldName(fieldName);

      const DIFFERENCE_MIN_VALUE = 0.000001;
      return values.map((value, index) => {
        const compareValue = compareValues[index];

        const difference = (value as number) - ((compareValue as number) || 0);
        // Super small values will show up as NaN when we try to format
        // them, so just show 0 instead
        if (Math.abs(difference) < DIFFERENCE_MIN_VALUE) {
          return 0;
        }
        return difference;
      });
    },
    [getComparisonValuesByFieldName, getValuesByFieldName]
  );

  return {
    data: parsedData,
    isLoading,
    getValuesByFieldName,
    getValueDifferencesByFieldName,
    getComparisonValuesByFieldName,
    rawResponse: {
      primaryData: data,
      comparisonData
    }
  };
};
